import React, {useEffect, useRef} from "react";
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import {initOutlineCity} from "../../utils/map/mapUtils";
import {createMiniMap, initMinimalMiniMap} from "../../utils/map/minimapUtils";
import {onlyUniqueByKey} from "../../utils/overlaysUtils";

const { default: VectorTileLayer } = require("../../utils/leaflet/VectorTileLayer-0.16.0.min");
const { layersRadioWithLegends } = require("../../utils/leaflet/Control.LayersRadioWithLegend");

export const MiniMapWithTiles = ({layers, locationInfos, outlineCity, initialZoom, minZoom, initialBaseLayer='grayscale', showLayersLegendControl=true}) => {
    const mapRef = useRef(undefined);
    const overlaysGroupRef = useRef(undefined);
    const outlineCityRef = useRef(undefined);
    const layersControlRef = useRef(undefined);
    const tooltipRef = useRef(undefined);


    useEffect(() => {
        mapRef.current = createMiniMap("minimap", minZoom);
        if (outlineCity) {
            outlineCityRef.current = initOutlineCity(outlineCity);
        }
        overlaysGroupRef.current = L.layerGroup().addTo(mapRef.current);

        initMinimalMiniMap(mapRef.current, outlineCityRef.current, locationInfos, null, initialZoom, initialBaseLayer);
    }, []);


    useEffect(() => {
        if (mapRef.current && layers) {
            overlaysGroupRef.current.clearLayers();

            const leafletLayers = layers.map((layer) => {
                const tilesId = layer.tilesId || layer.id;
                if (!layer.hasMissingData) {    // FIXME Handle missing data
                    //legend values & tile
                    return fetch(`/values/${tilesId}`)
                        .then(res => res.json())
                        .then(values => {
                            return layer.legend ? layer.legend(values) : values.filter(onlyUniqueByKey("label"));
                        })
                        .then(legends => {
                            const leafletLayer = VectorTileLayer(`/tiles/${tilesId}/{z}/{x}/{y}.pbf`, {
                                interactive: true,
                                vectorTileLayerStyles: {[tilesId]: (properties) => layer.style(properties, legends)},
                                attribution: layer.attributions ? `© ${layer.attributions}` : null,
                                zIndex: 10,
                                layer,
                                legends
                            })
                            if (layer.tooltip) {
                                leafletLayer.on('mouseover', (layerElement) => {
                                    const content = layer.tooltip(layerElement.layer.properties);
                                    if (content) {
                                        if (tooltipRef.current) {
                                            tooltipRef.current.remove();
                                        }
                                        tooltipRef.current = L.tooltip()
                                            .setLatLng(layerElement.latlng)
                                            .setContent(content)
                                            .addTo(mapRef.current);
                                    }
                                });
                                leafletLayer.on('mouseout', (layerElement) => {
                                    if (tooltipRef.current) {
                                        tooltipRef.current.remove();
                                        tooltipRef.current = undefined;
                                    }
                                });
                            }
                            leafletLayer.on('click', (layerElement) => {
                                const content = layer.popupContent(layerElement.layer.properties);
                                if (content) {
                                    L.popup()
                                        .setLatLng(layerElement.latlng)
                                        .setContent(content)
                                        .openOn(mapRef.current);
                                }
                                // Onclick event
                                if (layer.onclick) {
                                    layer.onclick(layerElement.layer.properties);
                                }
                                // Update feature style
                                if (layer.getFeatureId && layer.style) {
                                    leafletLayer.setFeatureStyle(
                                        layer.getFeatureId(layerElement.layer.properties),
                                        (properties) => layer.style(properties, legends)
                                    );
                                }
                            });
                            return [layer.label, leafletLayer];
                        })
                }
            });

            Promise.all(leafletLayers).then(layersList => {
                let overlays = Object.fromEntries(layersList);
                if (layersControlRef.current) {
                    layersControlRef.current.remove();
                }
                if (showLayersLegendControl) {
                    layersControlRef.current = layersRadioWithLegends(overlays, { collapsed: false, collapsible: false, position: 'topleft', autoZIndex: false }).addTo(mapRef.current);
                }
                // First layer is enabled by default
                if (layersList.length > 0) {
                    let leafletLayer = layersList[0][1];
                    overlaysGroupRef.current.addLayer(leafletLayer);
                }
            });
        }
    }, [layers]);

    return (
        <div className="dashboard__map">
            <div
                id={`minimap-map`}
                style={{ height: '100%'}}
                role="img"
                aria-label={`Carte de la ville : ${locationInfos.com.libelle}`}
            >
            </div>
        </div>
    )
}